import { resetCustomerUserPasswordApi } from "@/api/services/main/customer-user";
import { TCustomerUserDetailDto } from "@/api/services/main/customer-user/dtos/customer-user.dto";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { Modal } from "@/components/elements/Modal";
import { useCurrentUserRole } from "@/features/auth/hooks/useCurrentUserRole";
import { canResetPassword } from "@/features/customer-user/constant/statusHelpers";
import { ReadonlyField } from "@/features/customer-user/components/detail/ReadonlyField";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useDialog } from "@/hooks/useDialog";
import { text } from "@/theme/colors";
import { USER_ACCOUNT_STATUS_LABEL, USER_STATUS_LABEL, TUserAccountStatus, TUserStatus } from "@/types/enum";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { useQueryClient } from "@tanstack/react-query";
import { Stack, Typography } from "@mui/material";
import { FC } from "react";

export const UserStatusSection: FC<{ user: TCustomerUserDetailDto }> = ({ user }) => {
  const statusLabel = USER_STATUS_LABEL[user.status as TUserStatus] ?? user.status ?? "-";
  const accountStatusLabel = USER_ACCOUNT_STATUS_LABEL[user.accountStatus as TUserAccountStatus] ?? user.accountStatus ?? "-";
  const { canMutateUsers } = useCurrentUserRole();
  const queryClient = useQueryClient();
  const passwordResetDialog = useDialog();

  const handleResetPassword = async () => {
    const confirmed = await passwordResetDialog.show();
    if (!confirmed) return;

    try {
      await resetCustomerUserPasswordApi(user.id);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_DETAIL, String(user.id)] });
      toast.success("パスワードリセットメールを送信しました。");
    } catch (error) {
      showError(error);
    }
  };

  return (
    <InnerBox title="ユーザーの状態" padding="24px">
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap="24px" flexWrap="wrap">
        <Stack direction="row" gap="48px" flexWrap="wrap" alignItems="flex-start" flex={1}>
          <ReadonlyField label="ステータス" required value={statusLabel} />
          <ReadonlyField label="認証ステータス" value={accountStatusLabel} />
        </Stack>
        {canMutateUsers && (
          <Button variant="ghost" size="sm" sx={{ width: "158px" }} disabled={!canResetPassword(user.hasPassword, user.accountStatus)} onClick={handleResetPassword}>
            パスワードリセット
          </Button>
        )}
      </Stack>

      <Modal
        isOpen={passwordResetDialog.open}
        title="パスワードリセット"
        labelOk="リセットする"
        labelClose="キャンセル"
        onOk={passwordResetDialog.confirm}
        onClose={passwordResetDialog.cancel}
      >
        <Typography variant="cap12" color={text.secondary} py={2} component="div">
          登録済みパスワードがリセットされます。再登録のためのURLが本人にメールで通知されます。
          <br />
          続行してよろしいですか。
        </Typography>
      </Modal>
    </InnerBox>
  );
};
